import { BenefitIcon } from '@/components/BenefitIcon';
import { cn, formatCategory, toFixedTwo } from '@/lib/utils';
import { components } from '@/types/api';
import { useMemo } from 'react';
import EnrichedAmount from '@/assets/icons/enrichedAmount.svg?react';

type Receipt = components['schemas']['ReceiptOutput'];
type LineItem = components['schemas']['LineItem'];
interface ItemsListProps {
  receipt: Receipt;
  setLineItemDetails: (arg: LineItem) => void;
}

export default function ReceiptItemsList({ receipt, setLineItemDetails }: ItemsListProps) {
  return (
    <div className="px-4">
      <div
        className={cn('grid grid-cols-[1fr_0.2fr_0.2fr] items-center gap-2 py-2', {
          'grid-cols-[1fr_0.2fr_0.2fr]': receipt?.total_benefit !== '0.00',
          'grid-cols-[1fr_0.2fr]': receipt?.total_benefit == '0.00',
        })}
      >
        <p className="text-base font-bold">Total</p>
        {receipt?.total_benefit !== '0.00' ? (
          <>
            <p className="text-right text-base font-bold line-through">
              ${receipt?.transaction_amount}
            </p>
            <div className="flex flex-row items-center justify-end">
              <EnrichedAmount />
              <p className="text-right text-base font-bold">
                ${toFixedTwo(receipt?.revised_amount)}
              </p>
            </div>
          </>
        ) : (
          <p className="text-right text-base font-bold">
            ${toFixedTwo(receipt?.transaction_amount || '0')}
          </p>
        )}
      </div>
      {receipt?.line_items?.map((item) => {
        return (
          <div
            className={cn('border-primary-platinum grid items-center gap-2 border-b py-2', {
              'grid-cols-[1.3fr_0.3fr_0.3fr]': item?.total_benefit !== '0.00',
              'grid-cols-[1.3fr_0.3fr]': item?.total_benefit == '0.00',
            })}
            key={item.id}
            onClick={() => {
              setLineItemDetails(item);
            }}
          >
            <div>
              <p className="max-w-3xs truncate text-xs font-normal opacity-70">
                {item.description}
              </p>
              {item.category && (
                <p className="max-w-3xs truncate text-[8px] font-normal opacity-70">
                  {formatCategory(item.category)}
                </p>
              )}
            </div>
            {item?.total_benefit !== '0.00' ? (
              <>
                <p className="text-right text-xs font-normal line-through opacity-70">
                  ${toFixedTwo(item?.line_total)}
                </p>
                <div className="flex flex-row items-center justify-end">
                  <EnrichedAmount />
                  <p className="text-right text-xs font-bold">
                    ${toFixedTwo(item?.revised_amount)}
                  </p>
                </div>
              </>
            ) : (
              <p className="text-right text-xs font-bold">${toFixedTwo(item?.line_total)}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function BenefitsCell({
  item,
}: {
  item: Pick<LineItem, 'line_item_benefits' | 'line_total'>;
}) {
  const benefitAmountApplied = useMemo(() => {
    return item.line_item_benefits?.find((benefit) => benefit.applied_amount !== '0.00')
      ?.applied_amount;
  }, [item]);

  if (!benefitAmountApplied) {
    return null;
  }

  return (
    <>
      {item.line_total && (
        <p className="text-xs font-normal italic opacity-70">was ${item.line_total}</p>
      )}
      <div className="ml-1 flex items-center gap-1">
        {item.line_item_benefits &&
          item.line_item_benefits?.map((benefit, idx) => (
            <BenefitIcon
              index={idx}
              lineItemBenefit={benefit}
              key={benefit.account_id}
              hideBeneFitName
            />
          ))}
        {benefitAmountApplied && <span className="text-xs">${benefitAmountApplied}</span>}
      </div>
    </>
  );
}
